import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComingSoonCta from '@/components/feature/ComingSoonCta';

const tracks = [
  { number: '01', title: 'Ruido blanco puro', description: 'cobertura sonora uniforme' },
  { number: '02', title: 'Ruido rosado para dormir', description: 'más suave y profundo que el blanco' },
  { number: '03', title: 'Ruido marrón profundo', description: 'el más grave de los tres ruidos base' },
  { number: '04', title: 'Binaural Theta 4 Hz', description: 'para sueño profundo (requiere auriculares)' },
  { number: '05', title: 'Binaural Delta 2 Hz', description: 'para sueño aún más profundo (requiere auriculares)' },
  { number: '06', title: 'Binaural Alfa 8 Hz', description: 'para relajación previa al sueño (requiere auriculares)' },
  { number: '07', title: 'Ruido rosado + binaural', description: 'combinación para mayor efecto' },
  { number: '08', title: 'Ruido marrón + binaural', description: 'combinación más profunda' },
  { number: '09', title: 'Silencio activo 432 Hz', description: 'tono suave y mínimo' },
  { number: '10', title: 'La frecuencia del agua', description: 'ruido rosado con textura de río' },
];

const heroImage = 'https://readdy.ai/api/search-image?query=Digital%20art%20illustration%20in%20the%20style%20of%20premium%20editorial%20magazine%20cover%2C%20hyperrealistic%20digital%20render%20with%20soft%20painterly%20brush%20strokes%2C%20warm%20golden%20earth%20tones%2C%20soft%20textured%20lighting%2C%20abstract%20visualization%20of%20soft%20gentle%20brain%20waves%20and%20neural%20connections%20in%20warm%20terracotta%20and%20sage%20green%20colors%2C%20peaceful%20and%20scientific%20atmosphere%2C%20warm%20cream%20and%20terracotta%20color%20palette%2C%20artistic%20and%20serene%2C%20editorial%20premium%20quality%2C%20not%20a%20stock%20photo%2C%20not%20cold%20or%20clinical%2C%20warm%20neutral%20palette%2C%20abstract%20neuroscience%20art%2C%20calm%20and%20meditative%20mood%2C%20soft%20textures%2C%20binaural%20frequencies%20concept%2C%20neural%20harmony&width=1400&height=600&seq=273&orientation=landscape';

export default function AlbumFrecuenciasDelCerebro() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-cream">
      <Navbar />

      {/* Reading Progress Bar */}
      <div className="fixed top-0 left-0 w-full h-1 z-50">
        <div
          className="h-full bg-terracotta transition-all duration-150"
          style={{ width: '0%' }}
          id="reading-progress"
        />
      </div>

      {/* Hero Section */}
      <section className="relative pt-28 md:pt-36 pb-12 md:pb-20 px-6 md:px-10">
        <div className="w-full max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row gap-8 md:gap-16 items-start mb-10 md:mb-16">
            <div className="w-full md:w-1/2">
              <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
                {t('album_frecuencias.tag')}
              </span>
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-bold text-[#8B6F5C] leading-tight">
                {t('album_frecuencias.title1')}
                <br />
                <span className="font-bold">{t('album_frecuencias.title2')}</span>
              </h1>
            </div>
            <div className="w-full md:w-1/2 flex flex-col justify-end md:pt-12">
              <p className="font-sans text-base md:text-lg text-[#3D2B1F] leading-relaxed mb-6">
                {t('album_frecuencias.hero.desc')}
              </p>
              <div className="flex flex-wrap gap-3 mb-6">
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-brain-line text-xs" />
                  {t('album_frecuencias.badge1')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-user-smile-line text-xs" />
                  {t('album_frecuencias.badge2')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-music-line text-xs" />
                  {t('album.temas_badge')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-download-cloud-line text-xs" />
                  {t('album.descarga_badge')}
                </span>
                <span className="inline-flex items-center gap-1.5 font-sans text-xs font-medium text-[#5C4A3A] bg-white border border-sand/40 px-3 py-1.5 rounded-full">
                  <i className="ri-infinity-line text-xs" />
                  {t('album.uso_badge')}
                </span>
              </div>
              <div className="flex items-baseline gap-3 mb-6">
                <span className="font-serif text-4xl md:text-5xl font-bold text-[#8B6F5C]">$12.99</span>
                <span className="font-sans text-sm text-[#5C4A3A]">{t('common.usd')} — {t('common.pago_unico')}</span>
              </div>
              <ComingSoonCta />
            </div>
          </div>
          <div className="w-full h-[320px] md:h-[420px] rounded-3xl overflow-hidden">
            <img
              src={heroImage}
              alt="Visualización abstracta de ondas cerebrales en calma"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Sinopsis */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-8">
            <i className="ri-brain-line text-3xl text-cream" />
          </div>
          <h2 className="font-serif text-2xl md:text-4xl font-bold text-cream leading-tight mb-8">
            {t('album_frecuencias.sinopsis.title')}
          </h2>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed mb-6">
            Hay noches en que el cerebro de tu bebé no puede parar. No es que no quiera dormir. Es que no sabe cómo. Y en esas noches, la única ayuda que funciona es la que habla el mismo lenguaje del cerebro: las frecuencias.
          </p>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed mb-6">
            Este álbum no es música. Es neurociencia pura convertida en sonido. Cada pieza está construida alrededor de una frecuencia específica: delta para el sueño profundo, theta para la relajación, alpha para la calma. Frecuencias que el cerebro humano no puede ignorar. Frecuencias que, cuando se presentan correctamente, el cerebro las sigue. Las imita. Se sincroniza con ellas.
          </p>
          <p className="font-sans text-base md:text-lg text-cream leading-relaxed">
            No es magia. Es entrainment cerebral. Es la ciencia del cerebro siguiendo a un ritmo externo para encontrar su propio equilibrio. Y funciona. Especialmente en cerebros que todavía están aprendiendo a regularse solos.
          </p>
        </div>
      </section>

      {/* Track List */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <span className="inline-block font-sans text-xs font-semibold text-terracotta uppercase tracking-[0.2em] mb-4">
              {t('album.lista_temas')}
            </span>
            <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
              {t('album_frecuencias.tracks_title')}
            </h2>
            <p className="font-sans text-base text-[#3D2B1F] max-w-2xl mx-auto">
              Cada tema está construido alrededor de una frecuencia cerebral específica. No es música. Es un mapa sonoro del cerebro en calma.
            </p>
            <p className="font-sans text-sm text-terracotta font-medium mt-4">
              Los temas binaurales (04, 05, 06, 07, 08) requieren auriculares para funcionar correctamente.
            </p>
          </div>

          <div className="bg-white rounded-2xl border border-sand/40 overflow-hidden">
            {tracks.map((track, index) => (
              <div
                key={track.number}
                className={`flex items-center gap-4 md:gap-6 p-4 md:p-5 ${index !== tracks.length - 1 ? 'border-b border-sand/20' : ''}`}
              >
                <span className="font-sans text-sm font-medium text-[#5C4A3A] flex-shrink-0 w-8">
                  {track.number}
                </span>
                <div className="flex-1 min-w-0">
                  <h3 className="font-serif text-base md:text-lg font-bold text-[#8B6F5C]">
                    {track.title}
                  </h3>
                  <p className="font-sans text-sm text-[#5C4A3A] mt-0.5">
                    {track.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Cierre emocional */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-[#4A3F35]">
        <div className="w-full max-w-3xl mx-auto text-center">
          <div className="w-12 h-12 flex items-center justify-center mx-auto mb-8">
            <i className="ri-brain-line text-3xl text-cream" />
          </div>
          <p className="font-serif text-2xl md:text-3xl text-cream leading-relaxed mb-8">
            &ldquo;Hay noches en que el problema no es el cuerpo cansado, es la mente que no baja el volumen. Este álbum trabaja distinto a los demás: no es solo un sonido lindo de fondo, es una frecuencia diseñada para hablarle directamente al cerebro y ayudarlo a bajar, un escalón a la vez, hacia el descanso. Con auriculares, la diferencia se siente. Es una herramienta más precisa para esas noches en las que nada más alcanza.&rdquo;
          </p>
          <div className="w-16 h-px bg-cream/30 mx-auto mb-8" />
          <p className="font-sans text-base text-cream/80 leading-relaxed">
            — Mercedes, NeuroBabys
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-24 px-6 md:px-10 bg-cream">
        <div className="w-full max-w-3xl mx-auto text-center">
          <h2 className="font-serif text-3xl md:text-5xl font-bold text-[#8B6F5C] leading-tight mb-4">
            {t('album_frecuencias.cta.title')}
          </h2>
          <p className="font-sans text-base text-[#3D2B1F] max-w-xl mx-auto mb-8">
            {t('album_frecuencias.cta.desc')}
          </p>
          <div className="flex items-center justify-center gap-3 mb-8">
            <span className="font-serif text-5xl md:text-6xl font-bold text-[#8B6F5C]">$12.99</span>
            <span className="font-sans text-base text-[#5C4A3A]">USD</span>
          </div>
          <ComingSoonCta size="lg" className="mb-4" />
          <p className="font-sans text-sm text-[#5C4A3A]">
            {t('album.descarga_footer')}
          </p>
        </div>
      </section>

      <Footer />
    </div>
  );
}